/**
 * Save bid hook - 3-way dispatcher (NC, MAS Direct, MAS Indirect) + shared orchestration.
 */
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { bidAnalysisServices } from 'services/bidAnalysis/bidAnalysisServices';
import { buildNCSavePayload } from '../templates/NC/ncSavePayload';
import { buildMASDirectSavePayload } from '../templates/MASDirect/masDirectSavePayload';
import { buildMASIndirectSavePayload } from '../templates/MASIndirect/masIndirectSavePayload';
import type { UseSaveBidDeps } from './useSaveBid.types';

export function useSaveBid(deps: UseSaveBidDeps) {
  const {
    bidId,
    bidDetails,
    formData,
    yearFormDataRef,
    packSizeFormDataRef,
    packSizeTabs,
    gsmsHndlgType,
    analysisType,
    supplierName,
    selectedStatus,
    bidStatus,
    setBidStatus,
    setSelectedStatus,
    marketLandscapeData,
    calculateProgressWrapper,
    formatDateForAPI,
    setIsSaving,
    hasUserMadeChangesRef,
  } = deps;

  const handleSaveBid = useCallback(
    async (isAutoSave: boolean = false) => {
      if (!bidId) {
        console.error('No bid ID available');
        return;
      }

      setIsSaving(true);
      try {
        const currentTemplateType = bidDetails?.template_type || 'mas';
        const productType = bidDetails?.product_type;
        const isNC = currentTemplateType === 'nc';

        let formDataArray: Array<{ concept_name: string; pack_size: Record<string, any> }>;

        if (isNC) {
          formDataArray = buildNCSavePayload({
            packSizeTabs,
            latestYearFormData: yearFormDataRef.current,
            gsmsHndlgType,
            formatDateForAPI,
          });
        } else if (productType === 'direct') {
          formDataArray = buildMASDirectSavePayload({
            latestPackSizeFormData: packSizeFormDataRef.current,
            gsmsHndlgType,
            formatDateForAPI,
          });
        } else {
          formDataArray = buildMASIndirectSavePayload({
            latestPackSizeFormData: packSizeFormDataRef.current,
            gsmsHndlgType,
            formatDateForAPI,
          });
        }

        const contractsValue =
          formData.contracts && formData.contracts.trim() ? formData.contracts.trim() : null;
        const currentProgress = calculateProgressWrapper();

        const payload = {
          bid_master_id: parseInt(bidId),
          analystBADate: formatDateForAPI(formData.analysisDate || bidDetails?.analystBADate) || null,
          target_launch_date: formatDateForAPI(formData.targetLaunchDate) || null,
          analysis_type: analysisType?.toLowerCase().replace(' ', '-') || 'new-launch',
          supplier_name: supplierName || null,
          contracts: contractsValue,
          gsms_handling_type: gsmsHndlgType || null,
          status: selectedStatus,
          progress: currentProgress,
          form_details: { form_data: formDataArray },
          isActive: 1,
        };

        await bidAnalysisServices.saveBid(parseInt(bidId), payload);

        if (currentTemplateType === 'nc' && marketLandscapeData.length >= 0) {
          try {
            await bidAnalysisServices.updateMarketLandscape(parseInt(bidId), {
              market_landscape: marketLandscapeData,
            });
          } catch (marketLandscapeError) {
            console.error('Error saving market landscape in global save:', marketLandscapeError);
          }
        }

        if (selectedStatus !== bidStatus) {
          setBidStatus(selectedStatus);
        }

        if (isAutoSave) {
          toast.success('Bid saved', { autoClose: 2000 });
        } else {
          toast.success('Bid saved successfully!', { autoClose: 5000 });
        }

        if (!isAutoSave) {
          hasUserMadeChangesRef.current = false;
        }

        return true;
      } catch (error: any) {
        console.error('Error saving bid:', error);
        setSelectedStatus(bidStatus);

        if (!isAutoSave) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            'Failed to save bid. Please try again.';
          toast.error(`Failed to save bid: ${errorMessage}`, { autoClose: 5000 });
        }
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [
      bidId,
      bidDetails,
      formData,
      yearFormDataRef,
      packSizeFormDataRef,
      packSizeTabs,
      gsmsHndlgType,
      analysisType,
      supplierName,
      selectedStatus,
      bidStatus,
      setBidStatus,
      setSelectedStatus,
      marketLandscapeData,
      calculateProgressWrapper,
      formatDateForAPI,
      setIsSaving,
      hasUserMadeChangesRef,
    ]
  );

  return { handleSaveBid };
}
